/**
 * Hook for managing MCP servers and skills in the assistant.
 * Extracted from useAssistant to provide the same features
 * without conversation/streaming management.
 */
import { useState, useEffect, useCallback } from "react";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import { useMcpGuidelines } from "~/components/assistant/hooks/useMcpGuidelines";
import { useSkillsFeature } from "~/components/assistant/hooks/useSkillsFeature";
import type {
  MCPServer,
  PartialSkill,
  Skill,
  SkillCreate,
  SkillUpdate,
} from "~/components/assistant/types";
import { trackEvent } from "~/utils/monitoring";

const filterEnabled = (skills: PartialSkill[]) => skills.filter((s) => s.enabled);

interface UseAssistantV2FeaturesOptions {
  api: AssistantApi;
  contextId: string;
  enableSkills?: boolean;
}

export function useAssistantFeatures({
  api,
  contextId,
  enableSkills = false,
}: UseAssistantV2FeaturesOptions) {
  const skillsFeatureFlag = useSkillsFeature();
  const skillsEnabled = enableSkills && skillsFeatureFlag;

  const {
    hasAccepted: mcpGuidelinesAccepted,
    isAccepting: isAcceptingGuidelines,
    acceptGuidelines,
  } = useMcpGuidelines(api);

  // MCP Server state
  const [mcpServers, setMcpServers] = useState<MCPServer[]>([]);
  const [selectedMcpServers, setSelectedMcpServers] = useState<string[]>([]);

  // Skills state
  const [systemSkills, setSystemSkills] = useState<PartialSkill[]>([]);
  const [userSkills, setUserSkills] = useState<PartialSkill[]>([]);
  const [datasetSkills, setDatasetSkills] = useState<PartialSkill[]>([]);
  const [isLoadingSkills, setIsLoadingSkills] = useState(false);

  const toggleMcpServer = useCallback(
    (serverId: string) => {
      setSelectedMcpServers((prev) => {
        const isCurrentlySelected = prev.includes(serverId);
        const newSelection = isCurrentlySelected
          ? prev.filter((id) => id !== serverId)
          : [...prev, serverId];

        const server = mcpServers.find((s) => s.id === serverId);
        trackEvent("assistant_v2_mcp_server_toggled", {
          dataset_id: contextId,
          context: "dataset",
          server_id: serverId,
          server_name: server?.name || serverId,
          enabled: !isCurrentlySelected,
          total_selected: newSelection.length,
        });

        return newSelection;
      });
    },
    [mcpServers, contextId]
  );

  const loadSkills = useCallback(async () => {
    setIsLoadingSkills(true);
    try {
      const response = await api.listSkills(contextId);
      setSystemSkills(filterEnabled(response.system));
      setUserSkills(filterEnabled(response.user));
      setDatasetSkills(filterEnabled(response.dataset));
    } catch (error) {
      console.error("Failed to load skills:", error);
      setSystemSkills([]);
      setUserSkills([]);
      setDatasetSkills([]);
    } finally {
      setIsLoadingSkills(false);
    }
  }, [api, contextId]);

  const createSkill = useCallback(
    async (skill: SkillCreate) => {
      await api.createSkill(skill);
      await loadSkills();
    },
    [api, loadSkills]
  );

  const updateSkill = useCallback(
    async (skillId: string, skill: SkillUpdate) => {
      await api.updateSkill(skillId, skill);
      await loadSkills();
    },
    [api, loadSkills]
  );

  const deleteSkill = useCallback(
    async (skillId: string) => {
      await api.deleteSkill(skillId);
      await loadSkills();
    },
    [api, loadSkills]
  );

  const getSkill = useCallback(
    async (skillId: string): Promise<Skill> => {
      return api.getSkill(skillId);
    },
    [api]
  );

  // Load MCP servers on mount
  useEffect(() => {
    let cancelled = false;

    api
      .listMcpServers()
      .then((servers) => {
        if (cancelled) return;
        setMcpServers(servers);
        const defaultServerIds = servers.filter((s) => s.default).map((s) => s.id);
        setSelectedMcpServers(defaultServerIds);
      })
      .catch((error) => {
        console.error("Failed to load MCP servers:", error);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load skills when enabled or context changes
  useEffect(() => {
    if (!skillsEnabled) return;
    loadSkills();
  }, [skillsEnabled, loadSkills]);

  return {
    skillsEnabled,
    mcpServers,
    selectedMcpServers,
    toggleMcpServer,
    systemSkills,
    userSkills,
    datasetSkills,
    isLoadingSkills,
    createSkill,
    updateSkill,
    deleteSkill,
    getSkill,
    refreshSkills: loadSkills,
    mcpGuidelinesAccepted,
    isAcceptingGuidelines,
    acceptGuidelines,
  };
}
